import { useEffect } from 'react';
import { SITE_NAME, SITE_URL } from './site';

/**
 * Sets `document.title`, the description meta, and the canonical link for the
 * current page. `index.html` carries good defaults for the very first paint
 * (and for crawlers that don't execute JS); this keeps each public route
 * distinct for the ones that do — Google included.
 */
export function useSeo({
  title,
  description,
  path = '/',
}: {
  title: string;
  description?: string;
  path?: string;
}): void {
  useEffect(() => {
    // Don't double the brand: pages may pass a title that already contains it.
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} — ${SITE_NAME}`;
    document.title = fullTitle;

    const setMeta = (selector: string, attr: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement | HTMLLinkElement>(selector);
      if (!el) {
        el = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
        document.head.appendChild(el);
        if (selector.includes('property="og:')) {
          el.setAttribute('property', selector.match(/"([^"]+)"/)![1]!);
        } else if (selector.startsWith('link')) {
          el.setAttribute('rel', 'canonical');
        } else {
          el.setAttribute('name', selector.match(/"([^"]+)"/)![1]!);
        }
      }
      el.setAttribute(attr, content);
    };

    if (description) {
      setMeta('meta[name="description"]', 'content', description);
      setMeta('meta[property="og:description"]', 'content', description);
    }
    setMeta('meta[property="og:title"]', 'content', fullTitle);
    setMeta('link[rel="canonical"]', 'href', `${SITE_URL}${path}`);
    setMeta('meta[property="og:url"]', 'content', `${SITE_URL}${path}`);
  }, [title, description, path]);
}
